// Shared Reveal SDK loader — single place to manage CDN version and dependencies
// See: https://help.revealbi.io/web/install-client-sdk/
//
// Reveal 2.x removed the legacy `$.ig` / `RevealApi` browser globals. The client is
// now the `reveal-sdk` npm package; its IIFE bundle exposes a single global named
// `Reveal` (e.g. `window.Reveal.RevealView`), which is the drop-in replacement for
// every `$.ig.X` reference. jQuery and Day.js are no longer dependencies, so they are
// no longer loaded here. The `@revealbi/api` AI client is unchanged (still `window.rv`).

const REVEAL_VERSION = "2.1.0";
const REVEAL_CDN = `https://cdn.jsdelivr.net/npm/reveal-sdk@${REVEAL_VERSION}/dist/reveal-sdk.js`;
const REVEAL_API_CDN = `https://cdn.jsdelivr.net/npm/@revealbi/api@1.3.0/dist/index.umd.js`;
const MARKED_CDN = `https://cdn.jsdelivr.net/npm/marked/marked.min.js`;

// The client sends this cap as `maxCells` on every Dashboard/editor/widget/data request and
// the server truncates the result to `maxCells / columnCount` rows before sending it, so the
// SDK default of 100,000 stops a 37-column report at 2,702 rows. Nothing surfaces that: the
// grid header reads its count from a separate `totalRowCount` field the cap does not touch,
// so a truncated table still advertises the full 44,000. Export inherits it rather than
// escaping it — Dashboard/exportData formats the rows the browser already holds and never
// re-queries, which is why .csv and .xls come back short by the identical amount (rx#39810).
//
// 2,000,000 carries our widest report (~37 columns) to ~54,000 rows. It is a ceiling, not a
// prefetch: a widget whose result fits under it is untouched, so this changes behaviour only
// for the dashboards being silently truncated today. The cost lands on the request side —
// the export POSTs the whole table back up — which is why the ingress proxy-body-size and
// Kestrel's MaxRequestBodySize move with it.
const MAX_CELLS = 2_000_000;

// Keyed by src, so concurrent callers await one load rather than racing to start their own.
// This replaces a `document.querySelector('script[src=…]')` check, which asked whether a tag
// exists — not whether it has run. A second caller arriving while the 17MB SDK bundle is
// still in flight (a Turbo navigation onto another dashboard, or a controller reconnecting)
// got a resolved promise with `window.Reveal` still undefined. The map and the DOM have the
// same lifetime — one document — so it answers the same question, precisely.
const scriptLoads = new Map();

function loadScript(src) {
  const inFlight = scriptLoads.get(src);
  if (inFlight) return inFlight;

  const load = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    // jsDelivr answers with `access-control-allow-origin: *`, so the CORS fetch
    // succeeds and in-SDK exceptions arrive at window.onerror with a real message and
    // stack instead of an opaque "Script error." (which Sentry drops).
    script.crossOrigin = "anonymous";
    script.onload = resolve;
    // onerror hands us an Event, which reports as a contextless non-Error. Remove the
    // failed element so a subsequent retry appends a fresh <script> instead of matching
    // this dead one.
    script.onerror = () => {
      script.remove();
      reject(new Error(`Failed to load script: ${src}`));
    };
    document.head.appendChild(script);
  });

  // A failure must not be cached as this src's permanent answer: a later caller is
  // entitled to a fresh fetch rather than the old rejection.
  // Attaching this handler also marks the cached promise itself as handled, so callers
  // awaiting it later don't trigger an unhandled-rejection warning.
  load.catch(() => scriptLoads.delete(src));

  scriptLoads.set(src, load);
  return load;
}

export async function loadRevealSDK() {
  if (!window.Reveal?.RevealSdkSettings) {
    await loadScript(REVEAL_CDN);
  }
  // Reveal can exist while a later dependency is still loading or has failed.
  // Await each cached promise independently so concurrent callers and retries finish
  // the whole dependency chain, not just the first bundle.
  await loadScript(REVEAL_API_CDN);
  await loadScript(MARKED_CDN);

  // Named here for the same reason as the secure-context banner below: a bundle that loads
  // but defines nothing would otherwise surface as a bare TypeError on the next line, from a
  // helper the caller never mentioned.
  if (!window.Reveal?.RevealSdkSettings) {
    throw new Error(`Loaded ${REVEAL_CDN} but it exposed no window.Reveal.RevealSdkSettings`);
  }

  // Re-applied on the already-loaded path too: this is a static on the SDK, and a second
  // controller connecting must not be able to observe a different cap than the first.
  window.Reveal.RevealSdkSettings.maxCellsRestriction = MAX_CELLS;
}

// The vendored SDK bundle calls crypto.randomUUID, which browsers expose only in a
// secure context. On a plain-http dev origin (http://*.test) it throws inside the
// bundle and reaches us as an argument-less load failure, so name the cause here
// instead. Always false in staging/production, which are HTTPS.
export function renderInsecureContextError(element) {
  // Only an explicit false blocks. Browsers always define isSecureContext, but jsdom
  // leaves it undefined, and treating that as insecure would stop every controller
  // test at connect().
  if (window.isSecureContext !== false) return false;

  const url = `https://${window.location.host}${window.location.pathname}`;
  // eslint-disable-next-line no-console
  console.error(`Reveal BI requires a secure context; ${window.location.origin} is insecure so crypto.randomUUID is unavailable. Use ${url}`);

  const alert = document.createElement("div");
  alert.className = "alert alert-danger";

  const heading = document.createElement("strong");
  heading.textContent = "Reveal requires a secure context.";
  alert.append(heading, document.createTextNode(
    " This page is served over plain HTTP, so crypto.randomUUID is unavailable and the Reveal SDK cannot start. Open "
  ));

  const link = document.createElement("a");
  link.href = url;
  link.textContent = url;
  alert.append(link, document.createTextNode(" instead."));

  element.replaceChildren(alert);
  return true;
}

// Custom Scientist.com theme — matches the brand palette from _colors.scss.
// Colors are drawn from brand-primary (navy, teal), action (cyan), and status palettes.
// See: https://help.revealbi.io/web/theming-dashboards/
export function buildScientistTheme() {
  const theme = new window.Reveal.RevealTheme();

  // Chart series palette — 8 brand base colors interleaved with lighter variants.
  // This gives 16 distinct, harmonious colors before the SDK auto-generates.
  // Lighter variants are ~+20 lightness in HSL from the base.
  theme.chartColors = [
    "#2949a4", // brand-primary-teal-40 (navy blue)
    "#7a94cc", // navy blue +20L
    "#6fceb9", // brand-primary-cyan-62 (teal)
    "#adebdd", // primary-cyan-80 (light teal — from _colors.scss)
    "#547a9c", // brand-secondary-teal-47 (steel blue)
    "#9ab5cc", // steel blue +20L
    "#97c93b", // brand-secondary-citron-51 (citron green)
    "#c2e085", // citron +20L
    "#308fe8", // info-teal-55 (sky blue)
    "#85b4e0", // info-teal-70 (from _colors.scss)
    "#ec7513", // warning-orange-50 (orange)
    "#fbab6a", // warning-orange-70 (from _colors.scss)
    "#138da0", // brand-secondary-cyan-35 (dark teal)
    "#54c5d0", // cst-cyan-57 (from _colors.scss)
    "#5460d0", // cst-blue-57 (indigo)
    "#9a9ee3"  // indigo +20L
  ];

  // UI chrome
  theme.accentColor = "#2949a4";                  // brand-primary-teal-40 — buttons, selections
  theme.dashboardBackgroundColor = "#f5f5f5";     // neutral-96 — backoffice page background
  theme.visualizationBackgroundColor = "#ffffff";  // white cards
  theme.fontColor = "#333333";                     // neutral-20 — primary text
  theme.highlightColor = "#6fceb9";               // brand-primary-cyan-62 — forecasts, outliers

  // Typography — Source Sans 3 (Scientist.com brand font).
  //
  // Set EVERY slot. The SDK's default names ("Roboto-Italic", "Roboto-BoldItalic",
  // "Roboto-Mono-Regular") match no family we serve, and a font-family the browser
  // cannot resolve falls back to Times — which then sits in the page's font set
  // alongside Source Sans 3. Canvas text is shaped to Source Sans 3 glyph IDs and can
  // be painted from Times' glyph table, and the two disagree: SS3 orders `A`=2…`Z`=27,
  // `a`=28…`z`=53 with digits exiled to 1333+, while Times follows the standard
  // Macintosh order. Same index, different glyph — `a` renders as `9`, `o` as `G`, and
  // digits as symbols. Leaving a slot unset is enough; it never has to render.
  theme.regularFont = "Source Sans 3";
  theme.mediumFont = "Source Sans 3";
  theme.boldFont = "Source Sans 3";
  theme.italicFont = "Source Sans 3";
  theme.boldItalicFont = "Source Sans 3";
  theme.monoFont = "Source Sans 3";

  // Layout
  theme.useRoundedCorners = true;

  return theme;
}

// Module-scoped so a re-connecting controller can hand over its live token closure
// without replacing the singleton the chat controller caches.
let aiTokenProvider = null;

export function initializeRevealAI(hostUrl, tokenFn) {
  const RevealSdkClient = window.rv?.RevealSdkClient;
  if (!RevealSdkClient) return;

  aiTokenProvider = tokenFn;
  if (RevealSdkClient.isInitialized()) return;
  RevealSdkClient.initialize({ hostUrl, getBearerToken: () => aiTokenProvider?.() ?? "" });
}

// Vestigial. The SDK's auth interceptor resolves getBearerToken on every request and
// rewrites both the per-request header and defaultHeaders.Authorization, so a value
// written here never reaches the wire. Kept as the documented refresh hook for the AI
// client (rx-reveal/CLAUDE.md, "Two separate HTTP clients for auth").
export function updateRevealAIToken(token) {
  if (window.rv?.RevealSdkClient?.isInitialized()) {
    window.rv.RevealSdkClient.getInstance().setHeader("Authorization", `Bearer ${token}`);
  }
}
